import { useTranslation } from 'react-i18next';
import { SessionProps, useAuth } from 'hooks/use-auth';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useRegister } from 'hooks/use-register';
import { Event, isBirthdayValid } from 'utils/helpers';
import { GET_IN_USER_COOKIE_KEY, SESSION_EVENT } from 'utils/constants';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';

const gtagEventsObject = {
  social_name: 'getin_login_newuser_socialname_enter',
  email: 'getin_login_newuser_email_enter',
  gender: 'getin_login_newuser_gender_enter',
  birthday: 'getin_login_newuser_birthday_enter',
};

export const useRegisterHook = () => {
  const { t } = useTranslation();
  const { mutate, isLoading } = useRegister();
  const {
    session,
    handleIsOpenRegisterModal,
    isRegisterOpen,
    setAuthUser,
    setAuthSession,
    handleIsOpenConfirmEmailModal,
    handleIsOpenResendCodeModal,
    startedLoginByEmailOrSMS,
  } = useAuth();

  const currentFlowIsEmail =
    startedLoginByEmailOrSMS === SESSION_EVENT.LOGIN_BY_EMAIL;

  const schema = Yup.object().shape(
    {
      name: Yup.string().trim().required(t('@Register.full-name-required')),
      social_name: Yup.string().trim(),
      email: Yup.string().email(t('@Register.email-not-valid')).trim(),
      birthday: Yup.string()
        .nullable()
        .notRequired()
        .when('birthday', {
          is: (value: string) => value?.length,
          then: (rule) =>
            rule.test(
              'birthday-invalid',
              t('@Register.birthday-invalid'),
              isBirthdayValid
            ),
        }),
    },
    [['birthday', 'birthday']]
  );

  const addGtagEventInFields = (variables: typeof gtagEventsObject) => {
    Object.keys(variables).forEach((key) => {
      const value = key as keyof typeof gtagEventsObject;

      if (variables[value]) {
        Event.push(gtagEventsObject[value]);
      }
    });
  };

  const { values, handleChange, handleSubmit, errors, touched } = useFormik({
    initialValues: {
      name: '',
      social_name: '',
      email: '',
      birthday: '',
      gender: '',
    },
    validationSchema: schema,
    onSubmit: (variables) => {
      if (!session) return;

      currentFlowIsEmail
        ? Event.push('getin_login_email_entered')
        : Event.push('getin_login_phone_entered');

      addGtagEventInFields(variables);

      mutate(
        {
          currentFlow: currentFlowIsEmail ? 'email' : 'sms',
          params: {
            session: session?.registerSession || '',
            user: {
              ...variables,
              birthday: variables.birthday.split('/').reverse().join('-'),
            },
          },
        },
        {
          onSuccess: ({ data }) => {
            handleIsOpenRegisterModal(false);

            if (data.access_token) {
              setAuthUser(JSON.parse(Cookies.get(GET_IN_USER_COOKIE_KEY)!));
              return;
            }
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onError: (error: any) => {
            if (error.response.status === 412) {
              setAuthSession({
                registerSession: session.registerSession,
                phone: session.phone,
                email: values.email,
              } as SessionProps);

              handleIsOpenRegisterModal(false);
              handleIsOpenConfirmEmailModal(true);
              return;
            }

            toast.error(
              error?.response?.data?.message ||
                error?.response?.data?.errors?.general ||
                'Erro ao enviar os dados. Por favor, tente novamente.'
            );
          },
        }
      );
    },
  });

  const handleCloseRegisterModal = () => {
    handleIsOpenRegisterModal(false);
    setAuthSession(null);
  };

  const formikProps = (field: keyof typeof values) => ({
    value: values[field],
    error: touched[field] && errors[field] ? errors[field] : '',
    onChange: handleChange(field),
  });

  const handleClickButtonBack = () => {
    handleIsOpenRegisterModal(false);
    handleIsOpenResendCodeModal(true);
  };

  return {
    session,
    touched,
    errors,
    values,
    isLoading,
    isRegisterOpen,
    showInputEmail: currentFlowIsEmail,
    handleClickButtonBack,
    handleCloseRegisterModal,
    handleSubmit,
    handleChange,
    formikProps,
    t,
  };
};
